import PetBirthdayIcon from "@/app/components/ui/Reusable/PetBirthdayIcon";
import {
  ICartAddressDetails,
  ICartDetailsReponseCorrespondingPet,
  ICartSearchResponseData,
  IHDCartAddressListResponseData,
} from "@/api_framework/api_modals/Cart";
import { useImageConstants } from "@/app/hooks/useImageConstants";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { ICartState, setCartLevelCouponId } from "@/app_redux/reducers/slices/auth/cart_slice";
import React, { useEffect, useState } from "react";
import TableSkeleton from "@/app/components/ui/Skeletons/TableSkeleton";
import { useLoader } from "@/app/hooks/useLoader";
import NoItemFoundComponent from "@/app/components/ui/NoItemFoundComponent";
import { useNavigate } from "react-router";
import { ICustomerProfileState } from "@/app_redux/reducers/slices/auth/customer_profile_slice";
import { LuDog } from "react-icons/lu";
import { BsTruck } from "react-icons/bs";
import PetCartTable from "../PetCartTable";
import { useAppDispatch, useAppSelector } from "@/app_redux/hooks/root_hook";
import CartHDTable from "../CartHDTable";

export interface ICartTableFormPetItem extends ICartDetailsReponseCorrespondingPet {
  pet_id: string;
}

const CartCreationTableComponent: React.FC<{
  cartLevelCouponId: string;
  alreadyAddedURIDError: string | null;
  fetchLotDetails: (
    item: ICartSearchResponseData,
    swap?: boolean,
    editLotDetails?: boolean,
    isRefreshCart?: boolean,
  ) => void;
  selectedAddress?: ICartAddressDetails | null;
  currentAddress?: IHDCartAddressListResponseData | null;
}> = ({ alreadyAddedURIDError, cartLevelCouponId, fetchLotDetails, selectedAddress, currentAddress }) => {
  const { getRouteKey } = useRouter();
  const dispatch = useAppDispatch();
  const { NO_CART } = useImageConstants();
  const isCartTabeLoading = useLoader("isCustomerCartTableInProgress");
  const isHDCartTabeLoading = useLoader("isCustomerHDCartTableInProgress");
  const navigate = useNavigate();
  const [carts, setCarts] = useState<ICartTableFormPetItem[]>([]);
  const { activeCartDetails, activeCartId } = useRouteInfo(getRouteKey("CART_MANAGEMENT", "id"))?.routeState
    ?.state as ICartState;
  const { customerId, petId } = useRouteInfo(getRouteKey("CUSTOMER_PROFILE", "id")).routeState
    .state as ICustomerProfileState;
  const min = 1;
  const { mappedClinicName, mappedClinicId } = useAppSelector((state) => state.root.UIStates);
  const userLogInfo = useAppSelector((state) => state.auth?.user?.email);

  useEffect(() => {
    if (activeCartDetails) {
      setCarts(
        activeCartDetails?.pets
          ?.map((pet) => ({
            ...pet,
            items: pet.items
              ?.filter((item) => item.availability !== "unavailable")
              ?.map((item) => ({
                ...item,
                row_id: item.id,
                all_applicable_coupons: item.all_applicable_coupons,
              })),
          }))
          .filter((pet) => pet.items && pet.items.length > 0),
      );
      if (activeCartDetails?.cart_level_coupon_id) {
        dispatch(setCartLevelCouponId(activeCartDetails?.cart_level_coupon_id));
      } else {
        dispatch(setCartLevelCouponId(null));
      }
    }
  }, [activeCartDetails]);

  useEffect(() => {
    if (activeCartDetails?.status === "billed") {
      navigate(`/app/user-cart/cart-list/${customerId}?from_cart=true`);
    }
  }, [activeCartDetails]);

  const availableHdItems =
    activeCartDetails?.hd_cart?.main_cart_items?.filter((item) => item.availability !== "unavailable") || [];

  return (
    <div className="relative overflow-auto overscroll-contain mt-3 mx-2 pb-3 min-h-0 flex-1 cart-print-table-wrapper">
      <div className="bg-white rounded-xl shadow-md py-2">
        <div>
          {isCartTabeLoading ? (
            <TableSkeleton />
          ) : (
            carts?.map((cart) => {
              return (
                <div>
                  <div className="flex items-center pb-3">
                    <div className="rounded-l-lg pl-1">
                      <LuDog className="w-5 h-5" color="#f1380a" />
                    </div>
                    <p className="px-2 font-medium text-xs text-gray-500">
                      <span className="text-black font-semibold text-sm"> {cart?.pet_name}
                        <PetBirthdayIcon hasBirthday={cart?.has_birthday} petName={cart?.pet_name} />
                      </span>
                    </p>
                    {cart?.high_risk_patient && (
                      <div className="pl-5">
                        <p className="bg-[#fff5f6] rounded-2xl text-center px-5 py-2 text-[#d92500] font-medium">
                          {cart?.high_risk_patient}
                        </p>
                      </div>
                    )}
                    <div className="bg-[#e4e4e4] text-[#5b606d] py-1 px-3 rounded-md text-xs ml-5 cart-print-hide">
                      {activeCartDetails?.price_breakup?.number_of_items}
                    </div>
                  </div>
                  <PetCartTable
                    alreadyAddedURIDError={alreadyAddedURIDError}
                    cartLevelCouponId={cartLevelCouponId}
                    fetchLotDetails={fetchLotDetails}
                    activeCartId={activeCartId}
                    petCart={cart}
                    key={cart?.pet_id}
                    selectedAddress={selectedAddress}
                    currentAddress={currentAddress}
                  />
                </div>
              );
            })
          )}
        </div>
      </div>
      {availableHdItems.length > 0 && (
        <div className="w-full mt-2 p-2 bg-[#F05B72] rounded-2xl text-white shadow-[inset_0_4px_12px_rgba(255,255,255,0.7)]">
          <div className="flex flex-wrap justify-between items-center gap-2 mt-2 mb-4">
            <div className="flex items-center gap-2">
              <h2 className="flex items-center gap-2 font-semibold text-lg">
                <BsTruck className="w-5 h-5 ml-2 text-white" />
                <span className="font-bold text-[14px] mr-2">Home Delivery Items</span>
              </h2>
              <div className="flex items-center gap-3 flex-wrap">
                <span className="bg-white text-xs md:text-sm px-2 rounded-md">
                  <p className="text-gray-600 text-[10px]">{activeCartDetails?.hd_cart?.no_of_items} items</p>
                </span>
                <span className="bg-white text-xs md:text-sm px-2 rounded-md">
                  <p className="text-gray-600 text-[10px]">{activeCartDetails?.hd_cart?.delivery_time}</p>
                </span>
              </div>
            </div>
            <span className="font-bold text-[14px] mr-20">
              Total ₹ {activeCartDetails?.hd_cart?.total_price_of_items}
            </span>
          </div>
          <div className="overflow-y-visible bg-white rounded-xl shadow-sm">
            {isHDCartTabeLoading ? (
              <TableSkeleton />
            ) : (
              activeCartDetails?.hd_cart?.main_cart_items?.length > 0 && (
                <table className="w-full text-gray-800 text-sm md:text-base">
                  <thead className="bg-gray-50 border-b">
                    <tr className="sm:text-[12px] text-[12px]">
                      <th className="p-3 text-left font-medium">Goods</th>
                      <th className="p-3 text-left font-medium">Quantity</th>
                      <th className="p- text-left font-medium">Unit Price</th>
                      <th className="p-3 text-left font-medium">Gross</th>
                      <th className="p-3 text-left font-medium">Coupon</th>
                      <th className="p-3 text-left font-medium">Net</th>
                      <th className="p-3 text-center font-medium">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {availableHdItems.map((item, id) => (
                      <CartHDTable
                        item={item}
                        key={id}
                        min={min}
                        activeCartDetails={activeCartDetails}
                        activeCartId={activeCartId}
                        mappedClinicId={mappedClinicId}
                        currentAddress={currentAddress}
                        cartLevelCouponId={cartLevelCouponId}
                        customerId={customerId}
                        petId={petId}
                        mappedClinicName={mappedClinicName}
                        userLogInfo={userLogInfo}
                      />
                    ))}
                  </tbody>
                </table>
              )
            )}
          </div>
        </div>
      )}
      {!carts && carts?.length === 0 && availableHdItems?.length === 0 && (
        <NoItemFoundComponent
          message=""
          description="Add to cart and make your pet’s day!"
          imageURL={NO_CART}
          InputClassName="my-0 h-full"
        />
      )}
    </div>
  );
};

export default CartCreationTableComponent;
